import _ from 'lodash';
import { useEffect, useMemo, useReducer } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logBackIn } from '../actions/auth.actions';
import { HTTP_NOT_FOUND, HTTP_OK, HTTP_UNAUTHORIZED } from '../constants/http.constants';
import { NOT_FOUND_PATH } from '../constants/paths.constants';
import { dirDev } from '../lib/logger.utils';
import { USER_RESOURCE } from '../resources/user.resource';
import { fetchedSelector } from '../selectors/fetch.selectors';
import { usePrevious } from './usePrevious';

const REQUIRED_RESOURCE_FIELDS = ['key', 'api.getById', 'single.dataTransform'];

const validateResource = (config) => {
  REQUIRED_RESOURCE_FIELDS.forEach((field) => {
    if (!_.get(config, field)) {
      throw new Error(`${field} is required on a resource`);
    }
  });
};

const initialState = {
  isFetching: false,
  isFetched: false,
  isSubmitting: false,
  error: '',
  data: null
};

const actions = {
  fetchStart: 'fetchStart',
  fetchSuccess: 'fetchSuccess',
  fetchError: 'fetchError',
  submitStart: 'submitStart',
  submitSuccess: 'submitSuccess',
  submitError: 'submitError',
  refetch: 'refetch'
};

const resourceReducer = (state, action) => {
  let newState;
  switch (action.type) {
    case actions.fetchStart:
      newState = { ...state, isFetching: true };
      break;
    case actions.fetchSuccess:
      newState = {
        ...state,
        isFetching: false,
        isFetched: true,
        data: action.data
      };
      break;
    case actions.fetchError:
      newState = { ...state, isFetching: false, isFetched: true, error: action.error };
      break;
    case actions.submitStart:
      newState = {
        ...state,
        isSubmitting: true
      };
      break;
    case actions.submitSuccess:
      newState = {
        ...state,
        isSubmitting: false
      };
      if (action.data) {
        newState.data = { ...(state?.data ?? {}), ...action.data };
      } else {
        newState.data = { ...(state?.data ?? {}), [action.field]: action.value };
      }
      break;
    case actions.submitError:
      newState = {
        ...state,
        isSubmitting: false,
        error: ''
      };
      break;
    case actions.refetch:
      newState = {
        ...state,
        isFetched: false
      };
      break;

    default:
      throw new Error(`'${action.type}' action is not supported by resource reducer`);
  }
  return newState;
};

const select = (state, prop) => state?.[prop];

export const useSingleResource = (id, config) => {
  validateResource(config);
  const { single, api, updateFn } = useMemo(() => config, [config]);
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(resourceReducer, initialState);
  const prevState = usePrevious(state);
  const isUserFetched = useSelector((state) => fetchedSelector(state, { resource: USER_RESOURCE }));

  if (!_.isEqual(prevState, state)) {
    dirDev(`'${config.key}' state changed:`, state);
  }

  useEffect(() => {
    if ((single?.public || isUserFetched) && !state.isFetched && !state.isFetching) {
      dispatch({ type: actions.fetchStart });
      api
        .getById(id)
        .then((resp) => {
          switch (resp.status) {
            case HTTP_OK: {
              return dispatch({ type: actions.fetchSuccess, data: single.dataTransform(resp.json) });
            }
            case HTTP_NOT_FOUND: {
              return navigate(NOT_FOUND_PATH, { replace: true });
            }
            case HTTP_UNAUTHORIZED: {
              return logBackIn();
            }
            default: {
              return dispatch({ type: actions.fetchError, error: resp.json });
            }
          }
        })
        .catch((error) => dispatch({ type: actions.fetchError, error }));
    }
  }, [navigate, isUserFetched, state.isFetched, state.isFetching, id, api, single]);

  const getValue = (field) => {
    return single.dataPath || single.secondaryDataPath
      ? _.get(select(state, 'data'), single.dataPath)?.[field] ||
          _.get(select(state, 'data'), single.secondaryDataPath)?.[field]
      : select(state, single?.dataPath || single?.secondaryDataPath || 'data')?.[field];
  };

  const setValueInState = (value, field) => {
    dispatch({ type: actions.submitSuccess, value, field });
  };

  const setValue = (value, field, options) => {
    if (!updateFn) {
      dispatch({ type: actions.submitSuccess, value, field });
      options?.onSuccess?.();
      return;
    }

    dispatch({ type: actions.submitStart });
    updateFn(
      id,
      { [field]: value },
      {
        onSuccess: (resp) => {
          dispatch({ type: actions.submitSuccess, value, field });
          if (options.onSuccess) {
            options.onSuccess(resp);
          }
        },
        onError: (message) => {
          dispatch({ type: actions.submitError, error: message });
          if (options.onError) {
            options.onError(message);
          }
        }
      }
    );
  };

  const setMultipleValues = (body, options) => {
    if (!updateFn) {
      dispatch({ type: actions.submitSuccess, data: body });
      options?.onSuccess?.();
      return;
    }

    dispatch({ type: actions.submitStart });
    updateFn(id, body, {
      onSuccess: (resp) => {
        dispatch({ type: actions.submitSuccess, data: body });
        if (options.onSuccess) {
          options.onSuccess(resp);
        }
      },
      onError: (message) => {
        dispatch({ type: actions.submitError, error: message });
        if (options.onError) {
          options.onError(message);
        }
      }
    });
  };

  const refetch = () => {
    dispatch({ type: actions.refetch });
  };

  return {
    isFetching: select(state, 'isFetching'),
    isFetched: select(state, 'isFetched'),
    isSubmitting: select(state, 'isSubmitting'),
    data: select(state, 'data'),
    error: select(state, 'error'),
    refetch,
    getValue,
    setValue,
    setMultipleValues,
    setValueInState
  };
};
